// CRITICAL: Detect auth mode IMMEDIATELY before React renders
// This prevents redirect race conditions
// NOTE: Uses native console.log since logger is not available at module-level
(function detectAuthModeEarly() {
  const urlParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.substring(1));
  
  // Check ALL possible auth indicators
  const typeParam = urlParams.get('type') || hashParams.get('type');
  const tokenHash = urlParams.get('token_hash') || hashParams.get('token_hash');
  const accessToken = hashParams.get('access_token');
  const errorCode = urlParams.get('error_code') || hashParams.get('error_code');
  
  // Only log in development
  if (import.meta.env.DEV) {
    console.log('[App] Early detection params:', { type: typeParam, hasTokenHash: !!tokenHash, hasError: !!errorCode });
  }
  
  // If there's an error in the URL, clear auth modes
  if (errorCode) {
    if (import.meta.env.DEV) {
      console.log('[App] Error detected in URL, clearing auth modes');
    }
    sessionStorage.removeItem('password_recovery_mode');
    sessionStorage.removeItem('invite_mode');
    sessionStorage.removeItem('recovery_token_hash');
    return;
  }
  
  // INVITE flow (new user creating account)
  const isInviteFromURL = typeParam === 'invite';
  
  // RECOVERY flow (existing user resetting password)
  const isRecoveryFromURL = 
    typeParam === 'recovery' || 
    (!!tokenHash && typeParam !== 'invite') ||
    (!!accessToken && typeParam !== 'signup' && typeParam !== 'invite');
  
  if (isInviteFromURL) {
    if (import.meta.env.DEV) {
      console.log('[App] Early detection: INVITE mode detected');
    }
    sessionStorage.setItem('invite_mode', 'true');
    sessionStorage.removeItem('password_recovery_mode');
    
    if (tokenHash) {
      sessionStorage.setItem('recovery_token_hash', tokenHash);
    }
    
    localStorage.removeItem('sb-temtxtpbfjdxjzdoihwr-auth-token');
  } else if (isRecoveryFromURL) {
    if (import.meta.env.DEV) {
      console.log('[App] Early detection: RECOVERY mode detected');
    }
    sessionStorage.setItem('password_recovery_mode', 'true');
    sessionStorage.removeItem('invite_mode');
    
    if (tokenHash) {
      sessionStorage.setItem('recovery_token_hash', tokenHash);
    }
    
    localStorage.removeItem('sb-temtxtpbfjdxjzdoihwr-auth-token');
  }
})();

import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { FinanceProvider } from "@/context/FinanceContext";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import NotFound from "./pages/NotFound";
import Landing from "./pages/Landing";
import CleanTransactions from "./pages/CleanTransactions";
import Cadastros from "./pages/Cadastros";

import FluxoCaixa from "./pages/FluxoCaixa";
import RelatorioVendas from "./pages/RelatorioVendas";
import RelatorioRentabilidade from "./pages/RelatorioRentabilidade";
import AnaliseDespesas from "./pages/AnaliseDespesas";
import Recomendacoes from "./pages/Recomendacoes";
import ExtratoHierarquico from "./pages/ExtratoHierarquico";
import SaldosBancarios from "./pages/SaldosBancarios";
import Orcamento from "./pages/Orcamento";
import Auth from "./pages/Auth";
import SecurityDashboard from "./pages/SecurityDashboard";
import TeamSettings from "./pages/TeamSettings";
import ConciliacaoOFX from "./pages/ConciliacaoOFX";
import OAuthConsent from "./pages/OAuthConsent";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Carregando...</div>;
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return <>{children}</>;
}

function AppRoutes() {
  const { user, isRecoveryMode } = useAuth();
  
  // Check for recovery or invite mode in URL or sessionStorage
  const isInAuthSetupMode = () => {
    if (isRecoveryMode) return true;
    if (sessionStorage.getItem('password_recovery_mode') === 'true') return true;
    if (sessionStorage.getItem('invite_mode') === 'true') return true;
    const params = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const type = params.get('type') || hashParams.get('type');
    return type === 'recovery' || type === 'invite';
  };
  
  return (
    <Routes>
      <Route path="/auth" element={user && !isInAuthSetupMode() ? <Navigate to="/" replace /> : <Auth />} />
      <Route path="/" element={
        <ProtectedRoute>
          <Landing />
        </ProtectedRoute>
      } />
      <Route path="/lancamentos" element={
        <ProtectedRoute>
          <CleanTransactions />
        </ProtectedRoute>
      } />
      <Route path="/cadastros" element={
        <ProtectedRoute>
          <Cadastros />
        </ProtectedRoute>
      } />
      <Route path="/fluxo-caixa" element={
        <ProtectedRoute>
          <FluxoCaixa />
        </ProtectedRoute>
      } />
      <Route path="/relatorio-vendas" element={
        <ProtectedRoute>
          <RelatorioVendas />
        </ProtectedRoute>
      } />
      <Route path="/relatorio-rentabilidade" element={
        <ProtectedRoute>
          <RelatorioRentabilidade />
        </ProtectedRoute>
      } />
      <Route path="/analise-despesas" element={
        <ProtectedRoute>
          <AnaliseDespesas />
        </ProtectedRoute>
      } />
      <Route path="/recomendacoes" element={
        <ProtectedRoute>
          <Recomendacoes />
        </ProtectedRoute>
      } />
      <Route path="/extrato-hierarquico" element={
        <ProtectedRoute>
          <ExtratoHierarquico />
        </ProtectedRoute>
      } />
      <Route path="/saldos-bancarios" element={
        <ProtectedRoute>
          <SaldosBancarios />
        </ProtectedRoute>
      } />
      <Route path="/orcamento" element={
        <ProtectedRoute>
          <Orcamento />
        </ProtectedRoute>
      } />
      <Route path="/security" element={
        <ProtectedRoute>
          <SecurityDashboard />
        </ProtectedRoute>
      } />
      <Route path="/equipe" element={
        <ProtectedRoute>
          <TeamSettings />
        </ProtectedRoute>
      } />
      <Route path="/conciliacao" element={
        <ProtectedRoute>
          <ConciliacaoOFX />
        </ProtectedRoute>
      } />
      <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

const App: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AuthProvider>
        <FinanceProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </FinanceProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
